import { ref } from 'vue';
import { defineStore } from 'pinia';
import { openLink, isOneWeekPassedFrom } from '../utils/helpers';
import { setAuthState } from '../composables/auth';
import { update } from '../api/profile';
import { trackNudge } from '../api/nudge';
import { SHOPIFY_THEME } from '../storage';
import { CALENDLY_RESUMAI_URL, CALENDLY_JOBTRACKERAI_URL } from '../utils/constants';
import { SALES_GATED_LOCATIONS } from '../utils/locations';

/* global ShopifyBuy */

const FEATURES = {
	resumeAi: 'resum_ai',
	autoApply: 'auto_apply',
	jobTrackerAi: 'job_tracker_ai'
};

const FEATURE_NUDGE_MAP = {
	[FEATURES.resumeAi]: {
		utmLink: CALENDLY_RESUMAI_URL,
		dbFieldName: 'resume_ai_nudged_at'
	},
	[FEATURES.autoApply]: {
		utmLink: 'https://bit.ly/4dTFBXx',
		dbFieldName: 'auto_apply_nudged_at'
	},
	[FEATURES.jobTrackerAi]: {
		utmLink: CALENDLY_JOBTRACKERAI_URL,
		dbFieldName: 'job_tracker_ai_nudged_at'
	}
};

// Delay before the builder-entry nudge pops up, so the user sees their resume first.
const NUDGE_DELAY_MS = 10000;

const { getUser, setUser } = setAuthState();

export const useServicesNudges = defineStore('services-nudges', () => {
	const showModal = ref(false);
	const selectedFeature = ref(null);
	const ctaClicked = ref(false);
	const client = ref(null);
	const ui = ref(null);

	const updateNudgedAtFor = (feature) => {
		update({
			options: {
				[feature]: new Date()
			}
		}).then((response) => {
			setUser(response.data.data);
		});
	};

	const showNudgeModalFor = async (feature) => {
		const featureFieldName = FEATURE_NUDGE_MAP[feature]?.dbFieldName;

		if (getUser()?.options?.[featureFieldName] == null || isOneWeekPassedFrom(getUser()?.options?.[featureFieldName])) {
			selectedFeature.value = feature;
			showModal.value = true;
			updateNudgedAtFor(featureFieldName);
			trackNudge({ feature, event: 'shown' });
		}
	};

	// TODO: REVERT - Remove this method when builder-entry nudge experiment is reverted, and re-enable showNudgeModalFor call in pdf-header.vue
	const showNudgeOnceFor = (feature) => {
		const dbFieldName = FEATURE_NUDGE_MAP[feature]?.dbFieldName;
		const onceFieldName = `${dbFieldName}_once`;
		const user = getUser();

		// Skip if already shown once, user is subscribed, or user is not in allowed locations
		if (user?.options?.[onceFieldName] || user?.current_plan || !SALES_GATED_LOCATIONS.includes(user?.options?.location)) {
			return null;
		}

		return setTimeout(() => {
			selectedFeature.value = feature;
			showModal.value = true;
			trackNudge({ feature, event: 'shown' });

			update({
				options: {
					[onceFieldName]: new Date(),
					[dbFieldName]: new Date()
				}
			}).then((response) => {
				setUser(response.data.data);
			});
		}, NUDGE_DELAY_MS);
	};


	const ShopifyBuyInit = () => {
		client.value = ShopifyBuy.buildClient({
			domain: 'wonsulting.myshopify.com',
			storefrontAccessToken: '58c36d890bddc3cb0c484b7a7d10e1fb',
		});

		ui.value = ShopifyBuy.UI.init(client.value);
	};

	const generateShopifyBuyButton = (computedTiers, individualServices) => {
		ShopifyBuy.UI.onReady(client.value).then((shopifyUi) => {
			computedTiers.forEach((tier) => {
				shopifyUi.createComponent('product', {
					id: tier.price.productId,
					node: document.getElementById(tier.componentId),
					moneyFormat: '%24%7B%7Bamount%7D%7D',
					options: SHOPIFY_THEME,
				});
			});

			individualServices.forEach((individualService) => {
				shopifyUi.createComponent('product', {
					id: individualService.productId,
					node: document.getElementById(individualService.componentId),
					moneyFormat: '%24%7B%7Bamount%7D%7D',
					options: SHOPIFY_THEME,
				});
			});
		});

	};

	const nudgeCtaClickHandler = (feature) => {
		ctaClicked.value = true;
		openLink(FEATURE_NUDGE_MAP[feature].utmLink, true);
		trackNudge({ feature, event: 'cta_clicked' });

		const dbFieldName = FEATURE_NUDGE_MAP[feature]?.dbFieldName;

		update({
			options: {
				[`${dbFieldName}_cta_clicked`]: new Date(),
				[`${dbFieldName}_cta_clicked_at`]: new Date()
			}
		}).then((response) => {
			setUser(response.data.data);
		});
	};

	const closeModal = () => {
		// Track that user dismissed the nudge modal without clicking CTA
		const feature = selectedFeature.value;
		if (feature && !ctaClicked.value) {
			const dbFieldName = FEATURE_NUDGE_MAP[feature]?.dbFieldName;
			update({
				options: {
					[`${dbFieldName}_dismissed`]: new Date(),
					[`${dbFieldName}_dismissed_at`]: new Date()
				}
			}).then((response) => {
				setUser(response.data.data);
			});
		}
		ctaClicked.value = false;
		showModal.value = false;
	};

	return {
		showNudgeModalFor,
		showNudgeOnceFor,
		closeModal,
		nudgeCtaClickHandler,
		ShopifyBuyInit,
		generateShopifyBuyButton,

		selectedFeature,
		showModal,
		FEATURES
	};
});
